import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { TrendingUp, Loader2, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, Info } from "lucide-react";
import { toast } from "sonner";
import AppFooter from "@/components/layout/AppFooter";

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [resending, setResending] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  // Banner: ?verified=1 (success) or ?verified=0&reason=expired|invalid&email=...
  const params = new URLSearchParams(location.search);
  const verifiedFlag = params.get("verified");
  const verifyReason = params.get("reason");
  const verifyEmail = params.get("email") || "";
  useEffect(() => {
    if (verifyEmail && !email) setEmail(verifyEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyEmail]);

  const showVerifiedBanner = verifiedFlag === "1";
  const showVerifyError = verifiedFlag === "0";

  // When we get a "verify your email" error from the backend, show resend link.
  const needsResend = err && /verify your email/i.test(err);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      const dest = location.state?.from?.pathname || "/dashboard";
      navigate(dest, { replace: true });
    } catch (e) {
      setErr(e.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    if (!email) {
      toast.error("Enter your email above first");
      return;
    }
    setResending(true);
    try {
      await apiPost("/auth/resend-verification", { email });
      toast.success("Verification email sent. Check your inbox.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send verification email");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="login-page">
      <div className="flex-1 grid place-items-center px-4 py-10">
      <Card className="w-full max-w-md border bg-card">
        <CardHeader className="text-center space-y-1">
          <div className="mx-auto mb-2 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
            <TrendingUp className="h-6 w-6" />
          </div>
          <CardTitle className="font-display text-xl">CSE Investor</CardTitle>
          <CardDescription>Sign in to your account</CardDescription>
        </CardHeader>
        <CardContent>
          {showVerifiedBanner && (
            <div
              className="mb-4 flex items-start gap-2 rounded-md border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 px-3 py-2 text-xs text-[hsl(var(--success))]"
              data-testid="verified-banner"
            >
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                <strong>Email verified!</strong> Your account is now pending admin approval.
                You will be notified by email once it has been approved.
              </span>
            </div>
          )}
          {showVerifyError && (
            <div
              className="mb-4 flex items-start gap-2 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-2 text-xs text-[hsl(var(--warning))]"
              data-testid="verify-error-banner"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {verifyReason === "expired" ? (
                  <>This verification link has expired. Use the button below to resend.</>
                ) : (
                  <>This verification link is invalid or has already been used.</>
                )}
              </span>
            </div>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
                data-testid="login-email"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-[hsl(var(--primary))] hover:underline"
                  data-testid="link-forgot-password"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="login-password"
              />
            </div>
            {err && (
              <div
                role="alert"
                className="rounded-md border border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5 px-3 py-2 text-xs text-[hsl(var(--destructive))]"
                data-testid="login-error"
              >
                {err}
                {needsResend && (
                  <div className="mt-1.5">
                    <button
                      type="button"
                      onClick={onResend}
                      disabled={resending}
                      className="underline text-[hsl(var(--primary))]"
                      data-testid="resend-verification"
                    >
                      {resending ? "Sending…" : "Resend verification email"}
                    </button>
                  </div>
                )}
              </div>
            )}
            {(showVerifyError || needsResend) && !err && (
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2"
                onClick={onResend}
                disabled={resending}
                data-testid="resend-verification-btn"
              >
                {resending && <Loader2 className="h-4 w-4 animate-spin" />}
                Resend verification email
              </Button>
            )}
            <Button type="submit" disabled={busy} className="w-full gap-2" data-testid="login-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/register" className="text-[hsl(var(--primary))] hover:underline" data-testid="link-register">
              Create one
            </Link>
          </p>
        </CardContent>
      </Card>

      {/* Collapsible legal disclaimer banner — always visible below the card */}
      <div className="w-full max-w-md mt-4" data-testid="login-disclaimer-banner">
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 text-amber-900 dark:text-amber-200">
          <button
            type="button"
            onClick={() => setDisclaimerOpen((v) => !v)}
            className="w-full flex items-start gap-2 px-3 py-2.5 text-left text-xs"
            data-testid="login-disclaimer-toggle"
            aria-expanded={disclaimerOpen}
          >
            <Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
            <div className="flex-1 space-y-0.5">
              <div className="font-medium">Before you sign in</div>
              <div className="text-amber-900/80 dark:text-amber-200/80">
                For research only &mdash; not financial advice. Financial data may not
                be current or accurate. Always verify figures from official sources
                before investing.
              </div>
            </div>
            {disclaimerOpen ? <ChevronUp className="h-4 w-4 shrink-0 mt-0.5" /> : <ChevronDown className="h-4 w-4 shrink-0 mt-0.5" />}
          </button>
          {disclaimerOpen && (
            <div className="px-3 pb-3 pt-0 border-t border-amber-500/20 text-xs space-y-1.5 text-amber-900/90 dark:text-amber-200/90">
              <p>
                CSE Investor is a research platform. Nothing shown here is financial,
                investment, tax or legal advice. You are solely responsible for your
                investment decisions.
              </p>
              <p>
                Financial data is sourced from third parties (Yahoo Finance, SEC EDGAR,
                CSE publications) and may contain errors, delays or variances from
                actual market data.
              </p>
              <p>
                Read the{" "}
                <Link to="/terms" className="underline font-medium" data-testid="login-disclaimer-terms">Terms &amp; Conditions</Link>{" "}
                and{" "}
                <Link to="/privacy" className="underline font-medium" data-testid="login-disclaimer-privacy">Privacy Policy</Link>.
              </p>
            </div>
          )}
        </div>
      </div>
      </div>
      <AppFooter />
    </div>
  );
};

export default LoginPage;
